import { memo, useCallback, useLayoutEffect, useState } from 'react'

import { PersonAddAlt1, Refresh } from '@mui/icons-material'
import { Button, Tooltip } from '@mui/material'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { AnimatedCounterButton } from '@shared/components/MaterialUIEnhancements/IconButton'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, ListOperationBox } from '@shared/layouts'
import { RowActions, UpdateFunctionProps, useColumns } from 'domains/Organizations/Organization/AccessSettings'
import { InvitationActionButton } from 'domains/Organizations/Organization/AccessSettings/Invitations'
import { UserDialogContent } from 'domains/Organizations/Organization/AccessSettings/Members'
import { useOrganizationMemberUpdateMutation } from 'hooks/mutations/org'
import { usersOverviewRoute } from 'pages/NWPlatform/Organizations/Organization/Members'
import { organizationQueryKeys } from 'services/queryKeys'
import { resolveDisplayName } from 'utils/userInfo'

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const { organizationId } = usersOverviewRoute.useParams()
  const navigate = useNavigate({ from: usersOverviewRoute.fullPath })
  const { query } = usersOverviewRoute.useSearch()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [loadingRows, setLoadingRows] = useState<string[]>([])

  const organizationMemberUsersQuery = useInfiniteQuery({
    ...organizationQueryKeys.memberList({ organizationId, memberType: 'User', query }),
    refetchOnMount: 'always',
  })
  const userMembers = organizationMemberUsersQuery.data?.pages.flatMap((page) => page.items)

  const { mutate: updateUser, ...organizationMemberUpdateMutation } = useOrganizationMemberUpdateMutation()

  const handleUpdate = useCallback(
    ({ memberId, roles }: UpdateFunctionProps) => {
      updateUser({
        organizationId,
        memberId,
        organizationRoleRamMemberEdit: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          roles,
        },
      })
    },
    [organizationId, updateUser],
  )

  const columns = useColumns({
    onUpdate: handleUpdate,
  })

  useLayoutEffect(() => {
    if (organizationMemberUpdateMutation.isPending) {
      setLoadingRows([organizationMemberUpdateMutation.variables.memberId])

      return () => setLoadingRows([])
    }
  }, [organizationMemberUpdateMutation.isPending, organizationMemberUpdateMutation.variables?.memberId])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:My.Organization.searchUsers')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <ListOperationBox>
          <Tooltip title={t('refresh')} placement="left">
            <AnimatedCounterButton
              action={useCallback(
                () =>
                  queryClient.invalidateQueries({
                    queryKey: organizationQueryKeys.memberList({ organizationId, memberType: 'User' }).queryKey,
                  }),
                [organizationId, queryClient],
              )}
            >
              {(props) => <Refresh {...props} />}
            </AnimatedCounterButton>
          </Tooltip>

          <InvitationActionButton size="large" />

          <Button variant="contained" endIcon={<PersonAddAlt1 />} onClick={() => setDialogOpen(true)}>
            {t('domain:My.Organization.addPeople')}
          </Button>
        </ListOperationBox>
      </ActionStrip>

      <Table
        data={userMembers ?? []}
        columns={columns}
        isInitialLoadingGlobal={organizationMemberUsersQuery.isLoading}
        isInitialLoadingByRowByID={loadingRows.reduce((acc, row) => ({ ...acc, [row]: ['roles'] }), {})}
        ActionMenuProps={(row) => ({
          title: resolveDisplayName(row.original.userInfo) || '',
          entity: t('entity:org.member', 'Member', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
        tableOptions={{
          getRowId: (member) => member.id,
        }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <UserDialogContent />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
